/**
 * FR-CRM-6 / FR-CIN-1 — pinned segment and KPI definitions.
 *
 * A dashboard number that cannot be traced to a definition cannot be trusted,
 * so the definitions live here, versioned with the code, and thresholds are
 * inputs (settings, PRD Q8) rather than constants.
 */

export const SEGMENT_NAMES = [
  'new',
  'returning',
  'vip',
  'lost',
  'high_spender',
  'coupon_hunter',
  'cheese_lover',
  'beef_lover',
] as const

export type SegmentName = (typeof SEGMENT_NAMES)[number]

export interface SegmentThresholds {
  /** VIP needs both: visits and spend. */
  vipMinOrders: number
  vipMinSpentBdt: number
  /** "Lost" = no completed order in this many days. */
  lostAfterDays: number
  highSpenderMinBdt: number
  /** Share of orders that used a coupon (0–1). */
  couponHunterMinShare: number
  /** Share of items from a category that marks an affinity (0–1). */
  affinityMinShare: number
}

/** Staging defaults until PRD Q8 pins real ones. */
export const DEFAULT_SEGMENT_THRESHOLDS: SegmentThresholds = {
  vipMinOrders: 10,
  vipMinSpentBdt: 5000,
  lostAfterDays: 30,
  highSpenderMinBdt: 1500,
  couponHunterMinShare: 0.5,
  affinityMinShare: 0.4,
}

/** The per-customer facts segments are computed from — a rollup output. */
export interface CustomerSnapshot {
  completedOrders: number
  totalSpentBdt: number
  lastOrderAt: number | null
  ordersWithCoupon: number
  /** Item-count share by affinity keyword, e.g. { cheese: 0.6, beef: 0.1 }. */
  affinityShares: Partial<Record<'cheese' | 'beef', number>>
}

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Pure and total: every segment a customer belongs to right now. "New" and
 * "returning" are exclusive; everything else stacks.
 */
export function segmentsFor(
  snapshot: CustomerSnapshot,
  thresholds: SegmentThresholds,
  now: number,
): SegmentName[] {
  const result: SegmentName[] = []

  if (snapshot.completedOrders === 1) result.push('new')
  if (snapshot.completedOrders >= 2) result.push('returning')

  if (
    snapshot.completedOrders >= thresholds.vipMinOrders &&
    snapshot.totalSpentBdt >= thresholds.vipMinSpentBdt
  ) {
    result.push('vip')
  }

  if (
    snapshot.completedOrders > 0 &&
    snapshot.lastOrderAt !== null &&
    now - snapshot.lastOrderAt > thresholds.lostAfterDays * DAY_MS
  ) {
    result.push('lost')
  }

  if (snapshot.totalSpentBdt >= thresholds.highSpenderMinBdt) result.push('high_spender')

  if (
    snapshot.completedOrders >= 2 &&
    snapshot.ordersWithCoupon / snapshot.completedOrders >= thresholds.couponHunterMinShare
  ) {
    result.push('coupon_hunter')
  }

  if ((snapshot.affinityShares.cheese ?? 0) >= thresholds.affinityMinShare)
    result.push('cheese_lover')
  if ((snapshot.affinityShares.beef ?? 0) >= thresholds.affinityMinShare) result.push('beef_lover')

  return result
}
